import type {PhaSheet} from './pha-sheet.model';
import {DateTime} from 'luxon';

/** Presentation only: contract keys and protocol dates retain their canonical form. */
export function optionMessage(name: string, date: string, type?: string, strike?: number): string {
    const expiration = DateTime.fromISO(date || '').setLocale('en').toFormat('dd-MMM-yy');
    return [name, type, strike, date ? expiration : null].filter(value => value !== undefined && value !== null).join(' ');
}

export interface PhaContractConflict { key: string; description: string; rowIds: number[]; }

/** One listed contract, regardless of section, side, quantity or strategy membership. */
export function duplicateContracts(sheet: PhaSheet): PhaContractConflict[] {
    const identities = new Map<string, PhaContractConflict>();
    const add = (id: number, symbol: string, type: string, root: string, date: string, strike: number) => {
        if (!symbol || !date || strike == null || !isFinite(strike)) { return; }
        const key = [symbol.toUpperCase(), root.toUpperCase(), type, date, strike].join('|');
        const item = identities.get(key) || {key, description: optionMessage(sheet.displayNameForSymbol(symbol), date, type, strike), rowIds: []};
        item.rowIds.push(id); identities.set(key, item);
    };
    [...sheet.premiumRows, ...sheet.protectionRows].filter(r => !r.crossedOut && !r.removed).forEach(r => {
        const symbol = 'ticker' in r ? r.ticker : r.symbol;
        const root = r.expiration && r.expiration.descriptor && r.expiration.descriptor.expirationTicker
            || (r.optionTicker || '').split(' ')[0] || symbol;
        add(r.id, symbol, r.optionType, root, r.expiration && r.expiration.date,
            r.expectedStrike === undefined ? r.strike : r.expectedStrike);
    });
    sheet.rows.filter(r => r.assetType === 'Call' && !r.crossedOut).forEach(r =>
        add(r.id, r.callUnderlying, 'Call', r.callExpirationTicker || r.ticker.split(' ')[0], r.callExpirationDate, r.callStrike));
    return Array.from(identities.values()).filter(item => item.rowIds.length > 1);
}

/** Trial mutations stay off the live sheet; nested edits execute as one atomic operation. */
export function UniqueOptionEdit(_target: object, name: string, descriptor: PropertyDescriptor): void {
    const original = descriptor.value;
    descriptor.value = function(this: PhaSheet, ...args: any[]) {
        return this.validateOptionEdit(name, args, target => original.apply(target, args));
    };
}
